// =============================================================================
// App - Main router configuration (matches Figma routes)
// =============================================================================
// Uses React.lazy for route-based code splitting to improve initial load time
// v3.2: Added SessionValidator for proactive session validation on app load
// =============================================================================

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { AppLayout } from '@/components/layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToastContainer } from '@/components/ui/Toast';
import { SessionValidator } from '@/components/SessionValidator';

// =============================================================================
// Loading component for Suspense fallback
// =============================================================================

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009FE4]" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// =============================================================================
// Redirect helpers for legacy routes that need param forwarding
// (React Router v6 <Navigate> doesn't interpolate :params from parent routes)
// =============================================================================

function RedirectContactToBuyer() {
  const { id } = useParams();
  return <Navigate to={`/buyers/${id}`} replace />;
}

// Render the static investor/customer presentation deck inside a full-viewport
// iframe. The deck itself lives at public/transakt-presentation.html so it can
// also be opened directly; this route gives it a clean, shareable URL without
// the .html extension.
function PresentationPage() {
  return (
    <iframe
      src="/transakt-presentation.html"
      title="Transakt Presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        border: 0,
      }}
    />
  );
}

function RedirectSellerToBuyer() {
  const { id } = useParams();
  return <Navigate to={`/buyers/${id}`} replace />;
}

// =============================================================================
// lazyWithRetry — handles chunk load failures after deployments
// =============================================================================
// When a deployment replaces JS chunks, users with cached index.html get 404s
// on dynamic imports. This catches those errors and reloads the page to fetch
// the new index.html with correct chunk references. A 10-second cooldown
// prevents infinite reload loops if the server itself is broken.

function lazyWithRetry(
  importFn: () => Promise<{ default: React.ComponentType<any> }>
) {
  return lazy(() =>
    importFn().catch((error: unknown) => {
      const msg = error instanceof Error ? error.message : '';
      const name = error instanceof Error ? error.name : '';
      const isChunkError =
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Loading chunk') ||
        name === 'ChunkLoadError';

      if (isChunkError) {
        const lastReload = Number(sessionStorage.getItem('chunk_reload_at') || '0');
        if (Date.now() - lastReload > 10_000) {
          sessionStorage.setItem('chunk_reload_at', String(Date.now()));
          window.location.reload();
          return new Promise<never>(() => {}); // Never resolves — page is reloading
        }
      }
      throw error;
    })
  );
}

// =============================================================================
// Lazy-loaded pages (code splitting)
// =============================================================================

// Auth pages - loaded immediately since they're the first pages users see
import { LoginPage, VerifyPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth';

// Main pages - lazy loaded with retry for chunk load resilience
const HomePage = lazyWithRetry(() => import('@/pages/home/HomePage').then(m => ({ default: m.HomePage })));
const PropertiesPage = lazyWithRetry(() => import('@/pages/properties/PropertiesPage').then(m => ({ default: m.PropertiesPage })));
const BuyersPage = lazyWithRetry(() => import('@/pages/contacts/BuyersPage').then(m => ({ default: m.BuyersPage })));
const ContactDetailPage = lazyWithRetry(() => import('@/pages/contacts/ContactDetailPage').then(m => ({ default: m.ContactDetailPage })));
const PropertyDetailPage = lazyWithRetry(() => import('@/pages/properties/PropertyDetailPage').then(m => ({ default: m.PropertyDetailPage })));
const SupportPage = lazyWithRetry(() => import('@/pages/support/SupportPage').then(m => ({ default: m.SupportPage })));
const SettingsPage = lazyWithRetry(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
// v7 W6: My Pipeline — cross-listing offers view.
const PipelinePage = lazyWithRetry(() => import('@/pages/PipelinePage'));

// Public pages
const SharedPropertyPage = lazyWithRetry(() => import('@/pages/shared/SharedPropertyPage').then(m => ({ default: m.SharedPropertyPage })));
const VerifyEmailPage = lazyWithRetry(() => import('@/pages/shared/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));

// Internal pages
const DiagPage = lazyWithRetry(() => import('@/pages/internal/DiagPage').then(m => ({ default: m.DiagPage })));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to home if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (isAuthenticated()) {
    // v5.3: Honor returnTo so that already-authenticated users who click a
    // login link (e.g. "I have a Transakt account" on a shared property page)
    // land on their intended destination instead of being bounced to "/".
    // Restrict to internal paths to prevent open-redirect.
    const returnTo = new URLSearchParams(location.search).get('returnTo');
    if (returnTo && returnTo.startsWith('/')) {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      {/* SessionValidator proactively validates the server session on app load */}
      <SessionValidator>
        <Suspense fallback={<PageLoader />}>
          <Routes>
        {/* Public auth routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-login-code"
          element={
            <PublicRoute>
              <VerifyPage />
            </PublicRoute>
          }
        />
        {/* Keep /verify as alias for backwards compatibility */}
        <Route
          path="/verify"
          element={<Navigate to="/verify-login-code" replace />}
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          }
        />
        {/* Password auth v1 (2026-09) */}
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        {/* NOT PublicRoute-wrapped: a logged-in user clicking a reset link
            should reach the form, not get bounced into the app. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public shared property routes (no auth required) - Figma route */}
        <Route path="/shared/property/:shareId" element={<SharedPropertyPage />} />
        {/* Email verification route for returning guests (Phase 7) */}
        <Route path="/shared/property/:shareId/verify" element={<VerifyEmailPage />} />
        {/* Keep old route as alias for backwards compatibility */}
        <Route path="/share/:shareId" element={<Navigate to="/shared/property/:shareId" replace />} />

        {/* Investor / customer-facing presentation deck. Renders the static
            HTML (public/transakt-presentation.html) inside a full-viewport
            iframe so the URL stays clean and React Router-managed. */}
        <Route path="/transakt-presentation" element={<PresentationPage />} />

        {/* Protected routes with AppLayout - Figma routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Properties is the landing page */}
          <Route path="/" element={<Navigate to="/properties" replace />} />
          <Route path="/properties" element={<PropertiesPage />} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<HomePage />} />

          {/* Buyers - Figma: /buyers */}
          <Route path="/buyers" element={<BuyersPage />} />
          <Route path="/buyers/:id" element={<ContactDetailPage />} />

          {/* v7 W6: My Pipeline - cross-listing offers view */}
          <Route path="/pipeline" element={<PipelinePage />} />

          {/* Listings - redirect to /properties?tab=listings */}
          <Route path="/listings-and-sellers" element={<Navigate to="/properties?tab=listings" replace />} />

          {/* Property Detail - Figma: /properties/:id */}
          <Route path="/properties/:id" element={<PropertyDetailPage />} />

          {/* Support - Figma: /support */}
          <Route path="/support" element={<SupportPage />} />

          {/* Settings - Figma: /settings */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* Internal diagnostics (hidden, no nav link) */}
          <Route path="/internal/diag" element={<DiagPage />} />

          {/* Legacy routes - redirect to new Figma routes */}
          <Route path="/sellers" element={<Navigate to="/listings-and-sellers" replace />} />
          <Route path="/documents" element={<Navigate to="/" replace />} />
          <Route path="/contacts/:id" element={<RedirectContactToBuyer />} />
          <Route path="/sellers/:id" element={<RedirectSellerToBuyer />} />
        </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <ToastContainer />
      </SessionValidator>
    </ErrorBoundary>
  );
}

export default App;
